import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import type { components } from '@/../types/api'

export type RankingOut = components['schemas']['RankingOut']
export type RankingListItemOut = components['schemas']['RankingListItemOut']
export type RecomputeOut = components['schemas']['RecomputeOut']
export type StageOut = components['schemas']['StageOut']
export type CalibrationEntryOut = components['schemas']['CalibrationEntryOut']

export interface UseRankingParams {
  videoId: string
  lang: string
  /**
   * When set to an ISO timestamp, the query polls every 2s until the
   * server returns a ranking with ``computed_at`` strictly newer than
   * this value (or any ranking at all, if there was none before). Used
   * by the project page to follow a recompute-in-flight: ranking jobs
   * live on their own queue (``ranking_jobs``) and are not surfaced
   * through the public ``/jobs`` feed, so client-side polling is the
   * simplest way to know when the worker finished.
   */
  awaitNewerThan?: string | null
}

/**
 * Detailed ranking record for a single video. Richer than the slim
 * ``video.ranking`` carried by the list endpoint — this one includes the
 * full score ``breakdown``, guardrail violations, judge scores, and the
 * ``criteria_version`` the scoring ran under.
 */
export function useRanking({ videoId, lang, awaitNewerThan = null }: UseRankingParams) {
  return useQuery({
    queryKey: queryKeys.ranking.byId(videoId, lang),
    queryFn: async (): Promise<RankingOut | null> => {
      const { data } = await api.GET('/api/v1/rankings/{video_id}', {
        params: { path: { video_id: videoId }, query: { lang } },
      })
      if (!data) return null
      return data.data
    },
    enabled: Boolean(videoId && lang),
    staleTime: 30_000,
    refetchInterval: (query) => {
      if (!awaitNewerThan) return false
      const r = query.state.data
      if (!r || !r.computed_at) return 2_000
      return r.computed_at > awaitNewerThan ? false : 2_000
    },
  })
}

/**
 * Enqueue a ranking recompute. Returns ``{job_id, status}`` — the UI can
 * pick up the worker job from the shared jobs feed.
 */
/**
 * Channel-wide ranked backlog. Each row carries the same fields as a
 * single ranking lookup plus the video title (so the UI doesn't need a
 * second hop to /videos for the most common labels).
 */
export function useRankings(channel: string, limit = 100) {
  return useQuery({
    queryKey: queryKeys.ranking.list(channel, limit),
    queryFn: async (): Promise<RankingListItemOut[]> => {
      const { data } = await api.GET('/api/v1/rankings', {
        params: { query: { channel, limit } },
      })
      if (!data) throw new Error('rankings response empty')
      return data.data
    },
    enabled: Boolean(channel),
    staleTime: 30_000,
  })
}

/**
 * Calibration stage of a channel. Drives the side-rail card on the
 * Ranking page (warmup → first calibration → steady state).
 */
export function useStage(channel: string) {
  return useQuery({
    queryKey: queryKeys.ranking.stage(channel),
    queryFn: async (): Promise<StageOut> => {
      const { data } = await api.GET('/api/v1/rankings/stage', {
        params: { query: { channel } },
      })
      if (!data) throw new Error('stage response empty')
      return data.data
    },
    enabled: Boolean(channel),
    staleTime: 60_000,
  })
}

export function useCalibrations(channel: string, limit = 10) {
  return useQuery({
    queryKey: queryKeys.ranking.calibrations(channel, limit),
    queryFn: async (): Promise<CalibrationEntryOut[]> => {
      const { data } = await api.GET('/api/v1/rankings/calibrations', {
        params: { query: { channel, limit } },
      })
      if (!data) throw new Error('calibrations response empty')
      return data.data
    },
    enabled: Boolean(channel),
    staleTime: 60_000,
  })
}

/**
 * Force a calibration cycle. ``force`` bypasses the time-since-last guard
 * — useful when a developer wants to test a criteria_version bump.
 */
export interface TriggerCalibrationInput {
  channel: string
  force?: boolean
}

export function useTriggerCalibration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: TriggerCalibrationInput) => {
      const { data } = await api.POST('/api/v1/rankings/calibrate', {
        params: { query: { channel: input.channel, force: input.force ?? false } },
      })
      if (!data) throw new Error('calibrate response empty')
      return data
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: queryKeys.ranking.stage(vars.channel) })
      void qc.invalidateQueries({ queryKey: queryKeys.ranking.calibrations(vars.channel, 10) })
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
    },
  })
}

export function useRecomputeRanking() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: UseRankingParams): Promise<RecomputeOut> => {
      const { data } = await api.POST('/api/v1/rankings/{video_id}/recompute', {
        params: { path: { video_id: input.videoId }, query: { lang: input.lang } },
      })
      if (!data) throw new Error('ranking recompute empty')
      return data.data
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: queryKeys.ranking.byId(vars.videoId, vars.lang) })
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
    },
  })
}
